/**
 * Words for a candidate row. Pure, so each rule is readable on its own.
 *
 * Every instant is formatted in the ROW's IANA zone, never the browser's: a
 * diner in another zone reading "Opens Fri 06:00 p.m." must be reading the
 * venue's Friday evening.
 */

import type { Candidate, OpenState } from "./types.js";

/** `DINE_IN` reads "Dine in". A key is displayed, never compared to a literal. */
export function modeLabel(key: string): string {
  const spaced = key.replaceAll("_", " ").toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function addressLine(candidate: Candidate): string {
  const { line1, city, region } = candidate.address;
  return [line1, city, region].filter((part): part is string => part !== null && part !== "").join(", ");
}

export const OPEN_STATE_WORDS: Readonly<Record<OpenState, string>> = {
  OPEN: "Open",
  CLOSING_SOON: "Closing soon",
  CLOSED: "Closed",
};

export function formatOpensAt(instant: string, timeZone: string): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone, weekday: "short", hour: "2-digit", minute: "2-digit" }).format(
    new Date(instant),
  );
}

function formatTime(instant: string, timeZone: string): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone, hour: "2-digit", minute: "2-digit" }).format(new Date(instant));
}

/**
 * The soonest last-order cutoff among the modes that are closing soon, or
 * the chosen mode's own entry. CLOSING_SOON is the server's decision; this
 * only picks which cutoff to print.
 */
export function lastOrdersLine(candidate: Candidate, mode: string | null): string | null {
  const entries = candidate.modes.filter((entry) =>
    mode === null ? entry.openState === "CLOSING_SOON" : entry.mode === mode,
  );
  const cutoffs = entries
    .map((entry) => entry.lastOrderAt)
    .filter((at): at is string => at !== null)
    .sort((a, b) => Date.parse(a) - Date.parse(b));
  const soonest = cutoffs[0];
  return soonest === undefined ? null : `Last orders ${formatTime(soonest, candidate.timezone)}`;
}

export function distanceLine(distanceM: number | null): string | null {
  if (distanceM === null) return null;
  return distanceM < 1000 ? `${Math.round(distanceM)} m` : `${(distanceM / 1000).toFixed(1)} km`;
}
